// Shared option lists, label maps, and timestamp helpers for the Capture
// command room. Centralized so the data tables (column render + select filters)
// and the edit Sheets (form selects) draw from one source of truth — a new RFI
// type or interaction channel is added once here.
import { ProjectDataTypes } from '@mono/proto-clients';
// govcon.rfi_events.rfi_type enum.
export const RFI_TYPES = [
    {
        value: 'sources_sought',
        label: 'Sources sought'
    },
    {
        value: 'rfi',
        label: 'RFI'
    },
    {
        value: 'draft_rfp',
        label: 'Draft RFP'
    },
    {
        value: 'industry_day',
        label: 'Industry day'
    },
    {
        value: 'other',
        label: 'Other'
    }
];
// govcon.rfi_events.response_status enum: did we answer this RFI?
export const RFI_RESPONSE_STATUSES = [
    {
        value: 'pending',
        label: 'Pending'
    },
    {
        value: 'responded',
        label: 'Responded'
    },
    {
        value: 'not_responded',
        label: "Didn't respond"
    },
    {
        value: 'missed',
        label: 'Missed'
    }
];
// Statuses that take a "why" reason.
export const RFI_STATUSES_WITH_REASON = new Set([
    'not_responded',
    'missed'
]);
// govcon.customer_interactions.channel enum.
export const CHANNELS = [
    {
        value: 'meeting',
        label: 'Meeting'
    },
    {
        value: 'email',
        label: 'Email'
    },
    {
        value: 'call',
        label: 'Call'
    },
    {
        value: 'conference',
        label: 'Conference'
    },
    {
        value: 'other',
        label: 'Other'
    }
];
// govcon.capture_projects.stage enum, minus `converted` — that transition is
// owned by ConvertCaptureToProposal, not the generic stage editor.
export const STAGES = [
    {
        value: 'shaping',
        label: 'Shaping'
    },
    {
        value: 'qualifying',
        label: 'Qualifying'
    },
    {
        value: 'pursuing',
        label: 'Pursuing'
    },
    {
        value: 'no_bid',
        label: 'No bid'
    }
];
// govcon.customer_interactions.sentiment enum.
export const SENTIMENTS = [
    {
        value: 'positive',
        label: 'Positive'
    },
    {
        value: 'neutral',
        label: 'Neutral'
    },
    {
        value: 'negative',
        label: 'Negative'
    },
    {
        value: 'unknown',
        label: 'Unknown'
    }
];
// G-1: our bid position on the chase (not a competitor role).
export const BID_POSITIONS = [
    {
        value: 'prime',
        label: 'Prime'
    },
    {
        value: 'sub',
        label: 'Sub'
    },
    {
        value: 'jv',
        label: 'JV'
    }
];
// G-1: chase-state filter label — not capture_projects.stage and not a kanban.
export const STAGE_LABELS = [
    {
        value: 'qualifying',
        label: 'Qualifying'
    },
    {
        value: 'solutioning',
        label: 'Solutioning'
    },
    {
        value: 'pursuing',
        label: 'Pursuing'
    },
    {
        value: 'strategy',
        label: 'Strategy'
    },
    {
        value: 'bid_review',
        label: 'Bid review'
    }
];
export const CONTRACT_TYPES = [
    {
        value: 'T&M',
        label: 'T&M'
    },
    {
        value: 'FFP',
        label: 'FFP'
    },
    {
        value: 'hybrid',
        label: 'Hybrid'
    },
    {
        value: 'cost_plus',
        label: 'Cost+'
    }
];
export const COMPETITION_ROLES = [
    {
        value: 'incumbent',
        label: 'Incumbent'
    },
    {
        value: 'prime',
        label: 'Prime'
    },
    {
        value: 'sub',
        label: 'Sub'
    }
];
// govcon.competitive_field_participants.inclusion_basis enum.
export const INCLUSION_BASES = [
    {
        value: 'incumbent',
        label: 'Incumbent'
    },
    {
        value: 'prior_award',
        label: 'Prior award'
    },
    {
        value: 'capability_match',
        label: 'Capability match'
    },
    {
        value: 'market_signal',
        label: 'Market signal'
    },
    {
        value: 'customer_mention',
        label: 'Customer mention'
    },
    {
        value: 'teaming_signal',
        label: 'Teaming signal'
    }
];
// govcon.competitive_field_assessments.likelihood_band enum — qualitative, never numeric.
export const LIKELIHOOD_BANDS = [
    {
        value: 'possible',
        label: 'Possible'
    },
    {
        value: 'likely',
        label: 'Likely'
    },
    {
        value: 'confirmed',
        label: 'Confirmed'
    }
];
// govcon.competitive_field_assessments.assertion_status enum — B3.4-07.2 / B3.4-14.3:
// a hypothesis must never read as verified fact, so callers should prefer
// assertionStatusWording() over labelOf() for user-facing text.
export const ASSERTION_STATUSES = [
    {
        value: 'hypothesis',
        label: 'Hypothesis'
    },
    {
        value: 'corroborated',
        label: 'Corroborated'
    },
    {
        value: 'established',
        label: 'Established'
    }
];
// Shared low|medium|high confidence enum (assessments and evidence).
export const CONFIDENCE_LEVELS = [
    {
        value: 'low',
        label: 'Low'
    },
    {
        value: 'medium',
        label: 'Medium'
    },
    {
        value: 'high',
        label: 'High'
    }
];
// govcon.competitor_evidence.source_kind enum.
export const EVIDENCE_SOURCE_KINDS = [
    {
        value: 'rate_sheet',
        label: 'Rate sheet'
    },
    {
        value: 'public_award',
        label: 'Public award'
    },
    {
        value: 'web_reference',
        label: 'Web reference'
    },
    {
        value: 'internal_observation',
        label: 'Internal observation'
    },
    {
        value: 'manual_entry',
        label: 'Manual entry'
    }
];
// govcon.competitor_evidence.claim_type enum.
export const EVIDENCE_CLAIM_TYPES = [
    {
        value: 'strength',
        label: 'Strength'
    },
    {
        value: 'vulnerability',
        label: 'Vulnerability'
    },
    {
        value: 'customer_position',
        label: 'Customer position'
    },
    {
        value: 'likely_strategy',
        label: 'Likely strategy'
    },
    {
        value: 'capability',
        label: 'Capability'
    },
    {
        value: 'rate_or_price_inference',
        label: 'Rate/price inference (qualitative)'
    },
    {
        value: 'pricing_posture',
        label: 'Pricing posture (qualitative)'
    },
    {
        value: 'other',
        label: 'Other'
    }
];
// govcon.competitor_evidence.basis — a claim is sourced (reference + as-of
// date) or an explicit assumption; an assumption never reads as fact.
export const EVIDENCE_BASES = [
    {
        value: 'evidenced',
        label: 'Sourced evidence'
    },
    {
        value: 'assumption',
        label: 'Assumption'
    }
];
// govcon.competitor_evidence.stance enum — contradicting evidence must render
// distinguishably from supporting evidence (B3.4-07.2).
export const EVIDENCE_STANCES = [
    {
        value: 'supports',
        label: 'Supports'
    },
    {
        value: 'contradicts',
        label: 'Contradicts'
    }
];
// Explicit, unmistakable wording per assertion status (B3.4-07.2 / B3.4-14.3).
// A hypothesis must carry visible "not verified" wording, not just a color.
export function assertionStatusWording(status) {
    switch(status){
        case 'hypothesis':
            return 'Hypothesis — not verified';
        case 'corroborated':
            return 'Corroborated — partially verified';
        case 'established':
            return 'Established';
        default:
            return labelOf(ASSERTION_STATUSES, status);
    }
}
// govcon.capture_workstream_items.status enum.
export const WORKSTREAM_STATUSES = [
    {
        value: 'open',
        label: 'Open'
    },
    {
        value: 'in_progress',
        label: 'In progress'
    },
    {
        value: 'done',
        label: 'Done'
    },
    {
        value: 'parked',
        label: 'Parked'
    }
];
// G-2: action-plan task status — same tokens as workstreams, separate list.
// Not capture_projects.stage and not a kanban column.
export const TASK_STATUSES = WORKSTREAM_STATUSES;
// The 8 §4 workstreams.
export const WORKSTREAMS = [
    {
        value: ProjectDataTypes.CaptureWorkstreamType.CUSTOMER_SHAPING,
        label: 'Customer shaping'
    },
    {
        value: ProjectDataTypes.CaptureWorkstreamType.STRUCTURING_HYPOTHESIS,
        label: 'Structuring hypothesis'
    },
    {
        value: ProjectDataTypes.CaptureWorkstreamType.RFI_MARKET_RESEARCH,
        label: 'RFI / market research'
    },
    {
        value: ProjectDataTypes.CaptureWorkstreamType.SOLUTIONING,
        label: 'Solutioning'
    },
    {
        value: ProjectDataTypes.CaptureWorkstreamType.BLACK_HAT,
        label: 'Black hat'
    },
    {
        value: ProjectDataTypes.CaptureWorkstreamType.TEAM_BUILDING,
        label: 'Team building'
    },
    {
        value: ProjectDataTypes.CaptureWorkstreamType.HIRING_READINESS,
        label: 'Hiring readiness'
    },
    {
        value: ProjectDataTypes.CaptureWorkstreamType.CAPTURE_ARTIFACTS,
        label: 'Capture artifacts'
    }
];
// govcon.capture_plan_areas.health enum (CAP-01 / SAD-485) — human-owned,
// moves only through SetCapturePlanAreaHealth. Exactly five values; never
// inferred from a linked task's status.
export const CAPTURE_PLAN_HEALTH_STATUSES = Object.freeze([
    {
        value: 'not_started',
        label: 'Not Started'
    },
    {
        value: 'in_progress',
        label: 'In Progress'
    },
    {
        value: 'at_risk',
        label: 'At Risk'
    },
    {
        value: 'complete',
        label: 'Complete'
    },
    {
        value: 'not_applicable',
        label: 'Not Applicable'
    }
]);
// govcon.customer_theme_versions.category enum (SAD-491).
export const CUSTOMER_THEME_CATEGORIES = [
    {
        value: 'pain_point',
        label: 'Pain point'
    },
    {
        value: 'hot_button',
        label: 'Hot button'
    },
    {
        value: 'desired_outcome',
        label: 'Desired outcome'
    },
    {
        value: 'constraint',
        label: 'Constraint'
    },
    {
        value: 'other',
        label: 'Other'
    }
];
// govcon.customer_theme_versions.maturity enum (SAD-491).
export const CUSTOMER_THEME_MATURITIES = [
    {
        value: 'draft',
        label: 'Draft'
    },
    {
        value: 'emerging',
        label: 'Emerging'
    },
    {
        value: 'established',
        label: 'Established'
    }
];
// govcon.customer_theme_evidence.assertion_kind enum — fact vs inference
// (B3.2-04.1): distinguishes a documented customer statement from an
// internal assessment, an inference, or an unverified report.
export const CUSTOMER_THEME_EVIDENCE_ASSERTION_KINDS = [
    {
        value: 'customer_statement',
        label: 'Customer statement'
    },
    {
        value: 'internal_assessment',
        label: 'Internal assessment'
    },
    {
        value: 'inference',
        label: 'Inference'
    },
    {
        value: 'unverified_report',
        label: 'Unverified report'
    }
];
// govcon.customer_theme_evidence.source_kind enum (SAD-491) — a superset of
// customer_themes.origin_source (adds 'document').
export const CUSTOMER_THEME_EVIDENCE_SOURCE_KINDS = [
    {
        value: 'manual',
        label: 'Manual'
    },
    {
        value: 'ai_draft',
        label: 'AI draft'
    },
    {
        value: 'touchpoint',
        label: 'Touchpoint'
    },
    {
        value: 'notice_rfi_rfp',
        label: 'Notice / RFI / RFP'
    },
    {
        value: 'internal_note',
        label: 'Internal note'
    },
    {
        value: 'web_research',
        label: 'Web research'
    },
    {
        value: 'document',
        label: 'Document'
    }
];
// govcon.customer_themes.current_version_authorship wording (B3.2-05.4): a
// suggestion's authorship must read in text, never only by color.
export function themeAuthorshipWording(authorship) {
    return authorship === 'ai_suggested' ? 'AI-suggested' : 'human-proposed';
}
// govcon.pursuit_stakeholders.affiliation enum (SAD-491).
export const PURSUIT_STAKEHOLDER_AFFILIATIONS = [
    {
        value: 'customer',
        label: 'Customer'
    },
    {
        value: 'internal',
        label: 'Internal'
    },
    {
        value: 'partner',
        label: 'Partner'
    },
    {
        value: 'unknown',
        label: 'Unknown'
    }
];
// govcon.stakeholder_assessments.influence_band enum (SAD-491) — required on
// write (with rationale) so a row this surface authors can never itself be
// entirely blank across the masked sensitive layer.
export const STAKEHOLDER_INFLUENCE_BANDS = [
    {
        value: 'low',
        label: 'Low'
    },
    {
        value: 'medium',
        label: 'Medium'
    },
    {
        value: 'high',
        label: 'High'
    },
    {
        value: 'unknown',
        label: 'Unknown'
    }
];
// govcon.stakeholder_assessments.relationship_posture enum (SAD-491).
export const STAKEHOLDER_RELATIONSHIP_POSTURES = [
    {
        value: 'advocate',
        label: 'Advocate'
    },
    {
        value: 'neutral',
        label: 'Neutral'
    },
    {
        value: 'skeptic',
        label: 'Skeptic'
    },
    {
        value: 'blocker',
        label: 'Blocker'
    },
    {
        value: 'unknown',
        label: 'Unknown'
    }
];
// govcon.stakeholder_assessments.authorship enum (SAD-491) — same tokens as
// customer_theme_versions.authorship.
export const STAKEHOLDER_ASSESSMENT_AUTHORSHIPS = [
    {
        value: 'human',
        label: 'Human'
    },
    {
        value: 'ai_suggested',
        label: 'AI-suggested'
    }
];
// govcon.stakeholder_assessments.assessment_state enum (SAD-491) — 'accepted'
// requires authorship = 'human' at the write
// (chk_stakeholder_assessments_accepted_requires_human).
export const STAKEHOLDER_ASSESSMENT_STATES = [
    {
        value: 'suggested',
        label: 'Suggested'
    },
    {
        value: 'accepted',
        label: 'Accepted'
    }
];
// Look up an option's label, falling back to the raw value (then an em dash).
export function labelOf(options, value) {
    return options.find((o)=>o.value === value)?.label ?? value ?? '—';
}
export function workstreamLabel(t) {
    return WORKSTREAMS.find((w)=>w.value === t)?.label ?? 'Unspecified';
}
// proto Timestamp -> short local date string, tolerant of an unset/invalid ts.
export function formatTs(ts) {
    if (!ts) return '—';
    try {
        return ts.toDate().toLocaleDateString();
    } catch  {
        return '—';
    }
}
// proto Timestamp -> epoch ms for numeric sort (0 sorts unset dates first asc).
export function tsMs(ts) {
    if (!ts) return 0;
    try {
        return ts.toDate().getTime();
    } catch  {
        return 0;
    }
}
